// Typed API client. Single import point for every backend route.
//
// Conventions:
//
//   - One namespaced object per feature (`api.spaces.*`,
//     `api.channels.*`, ...). Methods take a discriminated input
//     and return a typed payload.
//   - Schema types (`@link/shared`) flow through unchanged on the
//     wire shape; we never reshape data here.
//   - Errors surface as `ApiCallError` from `fetchJson`. Callers
//     handle them with try/catch — the hooks in `state/data.tsx`
//     normalise them into UI-friendly empty states.
//
// This file is intentionally a thin sugar over `fetchJson`. The
// real coordination (caching, mutation, optimistic UI) belongs in
// the hooks layer above; this layer is just "talk to the API".

import type {
  // — auth —
  SignupChallengeRequest,
  SignupChallengeResponse,
  SignupRequest,
  SessionEstablishedResponse,
  LoginChallengeRequest,
  LoginChallengeResponse,
  LoginRequest,
  // — error envelope —
  ApiError,
} from "@link/shared";

import { fetchForm, fetchJson, type FetchOpts } from "./http";
import type { WireReaction } from "./message-reactions";

// ---------------------------------------------------------------------------
// Auth — handled in the AuthScreen flow but re-exported here for
// completeness so a single `api.auth.signup(...)` works elsewhere.
// ---------------------------------------------------------------------------

const auth = {
  signupChallenge(body: SignupChallengeRequest) {
    return fetchJson<SignupChallengeResponse>("/v1/auth/signup-challenge", {
      method: "POST",
      token: null,
      body,
    });
  },
  signup(body: SignupRequest) {
    return fetchJson<SessionEstablishedResponse>("/v1/auth/signup", {
      method: "POST",
      token: null,
      body,
    });
  },
  loginChallenge(body: LoginChallengeRequest) {
    return fetchJson<LoginChallengeResponse>("/v1/auth/login-challenge", {
      method: "POST",
      token: null,
      body,
    });
  },
  login(body: LoginRequest) {
    return fetchJson<SessionEstablishedResponse>("/v1/auth/login", {
      method: "POST",
      token: null,
      body,
    });
  },
  logout(opts?: FetchOpts) {
    return fetchJson<void>("/v1/auth/logout", { method: "POST", ...opts });
  },
};

// ---------------------------------------------------------------------------
// Me — the local user
// ---------------------------------------------------------------------------

export interface Identity {
  account_xid: string;
  username: string;
  username_change_available_at: string | null;
  display_name: string;
  avatar_url: string | null;
  banner_url: string | null;
  banner_color: string;
  banner_mode: "solid" | "gradient";
  banner_secondary_color: string;
  community_tag: CommunityTagSummary | null;
  custom_status: string | null;
  pronouns: string | null;
  presence_status: "online" | "away" | "busy" | "invisible" | "offline";
  member_since: string;
  badges?: string[];
}

const me = {
  identity() {
    return fetchJson<Identity>("/v1/me");
  },
  patch(body: Partial<Pick<Identity, "username" | "display_name" | "banner_url" | "banner_color" | "banner_mode" | "banner_secondary_color" | "custom_status" | "pronouns">>) {
    return fetchJson<Identity>("/v1/me", { method: "PATCH", body });
  },
  setPresence(status: Identity["presence_status"]) {
    return fetchJson<{ status: Identity["presence_status"]; presence: "online" | "away" | "busy" | "offline" }>(
      "/v1/me/presence",
      { method: "PATCH", body: { status } },
    );
  },
  uploadAvatar(file: File) {
    const form = new FormData();
    form.set("avatar", file);
    return fetchForm<{ avatar_url: string }>("/v1/me/avatar", form);
  },
  removeAvatar() {
    return fetchJson<{ avatar_url: null }>("/v1/me/avatar", { method: "DELETE" });
  },
  exportRequest(opts?: FetchOpts) {
    return fetchJson<{ ok: true; job_xid: string }>("/v1/me/export", {
      method: "POST",
      ...opts,
    });
  },
  scheduleDeletion(body: { account_xid: string }, opts?: FetchOpts) {
    return fetchJson<{ ok: true; deletion_scheduled_at: string }>(
      "/v1/me/delete",
      { method: "POST", body, ...opts },
    );
  },
  cancelDeletion(opts?: FetchOpts) {
    return fetchJson<{ ok: true }>("/v1/me/delete/cancel", {
      method: "POST",
      ...opts,
    });
  },
  setSidebarOrder(body: { space_xids_in_order: string[] }) {
    return fetchJson<{ ok: true }>("/v1/me/sidebar-order", {
      method: "PATCH",
      body,
    });
  },
};

// ---------------------------------------------------------------------------
// Spaces / Channels
// ---------------------------------------------------------------------------

export interface Space {
  xid: string;
  space_type: "personal_workspace" | "shared_workspace" | "community";
  name: string;
  icon_url: string | null;
  owner_account_id: string;
  notification_muted: boolean;
  created_at: string;
}

interface Channel {
  xid: string;
  name: string;
  channel_type: "text" | "snippets" | "secrets" | "files";
  archived?: boolean;
}

export interface CommunityTagSummary {
  xid: string;
  code: string;
  label: string;
  icon_url: string | null;
  space_xid: string;
  space_name: string;
}

export interface SpaceMember {
  account_id: string;
  username: string;
  display_name: string | null;
  nickname: string | null;
  avatar_url: string | null;
  banner_color: string;
  banner_mode: "solid" | "gradient";
  banner_secondary_color: string;
  pronouns: string | null;
  is_owner: boolean;
  is_admin: boolean;
  joined_at: string;
  presence: "online" | "away" | "busy" | "offline";
  badges: string[];
  roles: Array<{ xid: string; name: string; color: string }>;
  community_tag: CommunityTagSummary | null;
}

export interface SpaceRole {
  xid: string;
  space_xid: string;
  name: string;
  color: string;
  name_style: "solid" | "gradient" | "glow";
  secondary_color: string | null;
  sort_position: number;
  permissions: number;
}

export interface SpaceInvite {
  xid: string;
  created_by: { account_xid: string; display_name: string | null };
  created_at: string;
  expires_at: string;
  max_uses: number | null;
  use_count: number;
}

export interface SpaceBan {
  xid: string;
  account_xid: string;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
  reason: string | null;
  created_at: string;
}

export interface SpaceEmoji { xid: string; name: string; image_url: string; created_at: string }
export interface SpaceAuditEntry {
  xid: string; action: string; target_kind: string | null; target_xid: string | null;
  details: Record<string, unknown>; created_at: string;
  actor: { account_xid: string; display_name: string | null; username: string | null; avatar_url: string | null } | null;
}

const spaces = {
  list() {
    return fetchJson<{ spaces: Space[] }>("/v1/spaces");
  },
  create(body: {
    space_type: "shared_workspace" | "community";
    name: string;
    icon_url?: string;
  }) {
    return fetchJson<Space>("/v1/spaces", { method: "POST", body });
  },
  update(spaceXid: string, body: { name?: string; icon_url?: string | null }) {
    return fetchJson<Space>(`/v1/spaces/${encodeURIComponent(spaceXid)}`, {
      method: "PATCH",
      body,
    });
  },
  uploadIcon(spaceXid: string, file: File) {
    const form = new FormData();
    form.set("icon", file);
    return fetchForm<{ icon_url: string }>(
      `/v1/spaces/${encodeURIComponent(spaceXid)}/icon`,
      form,
    );
  },
  removeIcon(spaceXid: string) {
    return fetchJson<{ icon_url: null }>(
      `/v1/spaces/${encodeURIComponent(spaceXid)}/icon`,
      { method: "DELETE" },
    );
  },
  delete(spaceXid: string) {
    return fetchJson<void>(`/v1/spaces/${encodeURIComponent(spaceXid)}`, {
      method: "DELETE",
    });
  },
  setNotificationMuted(spaceXid: string, muted: boolean) {
    return fetchJson<{ muted: boolean }>(
      `/v1/spaces/${encodeURIComponent(spaceXid)}/notification-settings`,
      { method: "PATCH", body: { muted } },
    );
  },
  createInvite(spaceXid: string) {
    return fetchJson<{
      xid: string;
      space_xid: string;
      expires_at: string;
      max_uses: number | null;
      use_count: number;
      created_by: string;
      created_at: string;
    }>(`/v1/spaces/${encodeURIComponent(spaceXid)}/invites`, {
      method: "POST",
      body: { expires_in_seconds: 7 * 24 * 60 * 60, max_uses: 100 },
    });
  },
  acceptInvite(token: string) {
    return fetchJson<{ space: Space }>(
      `/v1/invites/${encodeURIComponent(token)}/accept`,
      { method: "POST" },
    );
  },
  channels(spaceXid: string) {
    return fetchJson<{ channels: Channel[] }>(
      `/v1/spaces/${encodeURIComponent(spaceXid)}/channels`,
    );
  },
  createChannel(
    spaceXid: string,
    body: {
      channel_type: "text" | "snippets" | "secrets" | "files";
      name: string;
      topic?: string;
    },
  ) {
    return fetchJson<Channel>(
      `/v1/spaces/${encodeURIComponent(spaceXid)}/channels`,
      { method: "POST", body },
    );
  },
  members(spaceXid: string) {
    return fetchJson<{ members: SpaceMember[] }>(
      `/v1/spaces/${encodeURIComponent(spaceXid)}/members`,
    );
  },
  roles(spaceXid: string) {
    return fetchJson<{ roles: SpaceRole[]; default_permissions: number }>(
      `/v1/spaces/${encodeURIComponent(spaceXid)}/roles`,
    );
  },
  updateDefaultPermissions(spaceXid: string, permissions: number) {
    return fetchJson<{ ok: true; default_permissions: number }>(
      `/v1/spaces/${encodeURIComponent(spaceXid)}/roles/default-permissions`,
      { method: "PATCH", body: { permissions } },
    );
  },
  createRole(spaceXid: string, body: { name: string; color: string; permissions: number; name_style?: SpaceRole["name_style"]; secondary_color?: string | null }) {
    return fetchJson<SpaceRole>(`/v1/spaces/${encodeURIComponent(spaceXid)}/roles`, { method: "POST", body });
  },
  updateRole(roleXid: string, body: { name?: string; color?: string; permissions?: number; sort_position?: number; name_style?: SpaceRole["name_style"]; secondary_color?: string | null }) {
    return fetchJson<SpaceRole>(`/v1/roles/${encodeURIComponent(roleXid)}`, { method: "PATCH", body });
  },
  deleteRole(roleXid: string) {
    return fetchJson<void>(`/v1/roles/${encodeURIComponent(roleXid)}`, { method: "DELETE" });
  },
  invites(spaceXid: string) {
    return fetchJson<{ invites: SpaceInvite[] }>(`/v1/spaces/${encodeURIComponent(spaceXid)}/invites`);
  },
  revokeInvite(inviteXid: string) {
    return fetchJson<void>(`/v1/invites/${encodeURIComponent(inviteXid)}`, { method: "DELETE" });
  },
  bans(spaceXid: string) {
    return fetchJson<{ bans: SpaceBan[] }>(`/v1/spaces/${encodeURIComponent(spaceXid)}/bans`);
  },
  ban(spaceXid: string, accountXid: string, body: { reason_category: "spam" | "compromised" | "rules" | "other"; reason?: string; delete_message_seconds: 0 | 3600 | 86400 | 604800 | -1 }) {
    return fetchJson<{ ok: true; ban_xid: string }>(`/v1/spaces/${encodeURIComponent(spaceXid)}/bans/${encodeURIComponent(accountXid)}`, { method: "POST", body: { ...body, reason: body.reason?.trim() || null } });
  },
  unban(spaceXid: string, banXid: string) {
    return fetchJson<void>(`/v1/spaces/${encodeURIComponent(spaceXid)}/bans/${encodeURIComponent(banXid)}`, { method: "DELETE" });
  },
  auditLog(spaceXid: string, cursor?: string) {
    const query = cursor === undefined ? "" : `?cursor=${encodeURIComponent(cursor)}`;
    return fetchJson<{ entries: SpaceAuditEntry[]; next_cursor: string | null }>(`/v1/spaces/${encodeURIComponent(spaceXid)}/audit-log${query}`);
  },
  emojis(spaceXid: string) {
    return fetchJson<{ emojis: SpaceEmoji[] }>(`/v1/spaces/${encodeURIComponent(spaceXid)}/emojis`);
  },
  uploadEmoji(spaceXid: string, name: string, file: File) {
    const form = new FormData(); form.set("name", name); form.set("emoji", file);
    return fetchForm<SpaceEmoji>(`/v1/spaces/${encodeURIComponent(spaceXid)}/emojis`, form);
  },
  deleteEmoji(spaceXid: string, emojiXid: string) {
    return fetchJson<void>(`/v1/spaces/${encodeURIComponent(spaceXid)}/emojis/${encodeURIComponent(emojiXid)}`, { method: "DELETE" });
  },
  patchMember(
    spaceXid: string,
    accountXid: string,
    body: { is_admin?: boolean; nickname?: string | null },
  ) {
    return fetchJson<SpaceMember>(
      `/v1/spaces/${encodeURIComponent(spaceXid)}/members/${encodeURIComponent(accountXid)}`,
      { method: "PATCH", body },
    );
  },
  removeMember(spaceXid: string, accountXid: string) {
    return fetchJson<void>(
      `/v1/spaces/${encodeURIComponent(spaceXid)}/members/${encodeURIComponent(accountXid)}`,
      { method: "DELETE" },
    );
  },
  transferOwner(spaceXid: string, accountXid: string) {
    return fetchJson<{ ok: true; owner_account_id: string }>(
      `/v1/spaces/${encodeURIComponent(spaceXid)}/transfer-owner`,
      { method: "POST", body: { new_owner_account_id: accountXid } },
    );
  },
  setMemberRoles(spaceXid: string, accountXid: string, roleXids: string[]) {
    return fetchJson<{ ok: true }>(
      `/v1/spaces/${encodeURIComponent(spaceXid)}/members/${encodeURIComponent(accountXid)}/roles`,
      { method: "PUT", body: { role_ids: roleXids } },
    );
  },
};

export interface CommunityTagPreview {
  xid: string;
  code: string;
  label: string;
  icon_url: string | null;
  space: {
    xid: string;
    name: string;
    icon_url: string | null;
    description: string | null;
    member_count: number;
    discovery_enabled: boolean;
  };
  is_member: boolean;
}

const communityTags = {
  list() {
    return fetchJson<{
      tags: Array<CommunityTagSummary & { space_icon_url: string | null }>;
      equipped_tag_xid: string | null;
    }>("/v1/me/community-tags");
  },
  preview(tagXid: string) {
    return fetchJson<CommunityTagPreview>(
      `/v1/community-tags/${encodeURIComponent(tagXid)}`,
    );
  },
  save(
    spaceXid: string,
    body: { code: string; label: string; icon_url?: string | null },
  ) {
    return fetchJson<{ xid: string; code: string; label: string; iconUrl: string | null }>(
      `/v1/spaces/${encodeURIComponent(spaceXid)}/community-tag`,
      { method: "PUT", body },
    );
  },
  equip(tagXid: string) {
    return fetchJson<{ ok: true }>("/v1/me/community-tag", {
      method: "POST",
      body: { tag_xid: tagXid },
    });
  },
  unequip() {
    return fetchJson<void>("/v1/me/community-tag", { method: "DELETE" });
  },
  join(tagXid: string) {
    return fetchJson<{ ok: true; space_xid: string }>(
      `/v1/community-tags/${encodeURIComponent(tagXid)}/join`,
      { method: "POST" },
    );
  },
};

const channels = {
  get(channelXid: string) {
    return fetchJson<Channel>(`/v1/channels/${encodeURIComponent(channelXid)}`);
  },
  pins(channelXid: string) {
    return fetchJson<{ messages: WireMessage[] }>(
      `/v1/channels/${encodeURIComponent(channelXid)}/pins`,
    );
  },
  patch(
    channelXid: string,
    body: { name?: string; topic?: string | null; sort_position?: number; archived?: boolean },
  ) {
    return fetchJson<Channel>(`/v1/channels/${encodeURIComponent(channelXid)}`, {
      method: "PATCH",
      body,
    });
  },
  delete(channelXid: string) {
    return fetchJson<void>(`/v1/channels/${encodeURIComponent(channelXid)}`, {
      method: "DELETE",
    });
  },
};

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

/**
 * Wire shape for a message row. Discriminated by `storage_kind` to
 * mirror the server contract.
 */
export interface WireMessage {
  xid: string;
  channel_xid: string;
  author_account_xid: string;
  author_display_name: string;
  author_avatar_url: string | null;
  created_at: string;
  edited_at: string | null;
  storage_kind: "e2e" | "atrest";
  /** Plaintext body when `storage_kind === "atrest"`; null otherwise. */
  body: string | null;
  /** Opaque ciphertext envelope when `storage_kind === "e2e"`. */
  envelope: string | null;
  encryption_context_id?: string;
  reactions?: WireReaction[];
  pinned?: boolean;
  parent_message_xid?: string;
  expires_at?: string;
}

interface ServerMessage {
  xid: string;
  channel_xid: string;
  author: {
    account_id: string | null;
    display_name: string | null;
    avatar_url: string | null;
  };
  parent_message_xid: string | null;
  created_at: string;
  edited_at: string | null;
  expires_at: string | null;
  storage_kind: "e2e" | "atrest";
  reactions: Array<{ emoji: string; account_ids: string[] }>;
  envelope_b64?: string;
  e2e_context_id?: string | null;
  plaintext?: string;
}

function normalizeMessage(message: ServerMessage): WireMessage {
  const authorExists = message.author.account_id !== null;
  return {
    xid: message.xid,
    channel_xid: message.channel_xid,
    author_account_xid: message.author.account_id ?? "deleted",
    author_display_name: authorExists
      ? (message.author.display_name ?? "Link user")
      : "Deleted user",
    author_avatar_url: message.author.avatar_url,
    created_at: message.created_at,
    edited_at: message.edited_at,
    storage_kind: message.storage_kind,
    body: message.storage_kind === "atrest" ? (message.plaintext ?? "") : null,
    envelope: message.storage_kind === "e2e" ? (message.envelope_b64 ?? null) : null,
    ...(message.e2e_context_id !== undefined && message.e2e_context_id !== null
      ? { encryption_context_id: message.e2e_context_id }
      : {}),
    reactions: message.reactions.map((reaction) => ({
      emoji: reaction.emoji,
      count: reaction.account_ids.length,
      mine: false,
      accountIds: [...new Set(reaction.account_ids)],
    })),
    ...(message.parent_message_xid !== null
      ? { parent_message_xid: message.parent_message_xid }
      : {}),
    ...(message.expires_at !== null ? { expires_at: message.expires_at } : {}),
  };
}

function messageRequest(body: SendMessageBody) {
  if (body.storage_kind === "atrest") {
    return {
      storage_kind: "atrest" as const,
      plaintext: body.body,
      ...(body.parent_message_xid !== undefined
        ? { parent_message_xid: body.parent_message_xid }
        : {}),
      ...(body.attachment_xids !== undefined
        ? { attachment_xids: body.attachment_xids }
        : {}),
      ...(body.mentions !== undefined ? { mentions: body.mentions } : {}),
    };
  }
  return {
    storage_kind: "e2e" as const,
    envelope_b64: body.envelope,
    envelope_kind: body.envelope_kind ?? "e2e",
    e2e_context_id: body.e2e_context_id,
    ...(body.parent_message_xid !== undefined
      ? { parent_message_xid: body.parent_message_xid }
      : {}),
    ...(body.expires_at !== undefined ? { expires_at: body.expires_at } : {}),
    ...(body.attachment_xids !== undefined
      ? { attachment_xids: body.attachment_xids }
      : {}),
  };
}

const messages = {
  list(channelXid: string, opts?: { before?: string; limit?: number }) {
    const qs = new URLSearchParams();
    if (opts?.before !== undefined) qs.set("cursor", opts.before);
    if (opts?.limit !== undefined) qs.set("limit", opts.limit.toString());
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return fetchJson<{ messages: ServerMessage[]; next_cursor: string | null }>(
      `/v1/channels/${encodeURIComponent(channelXid)}/messages${suffix}`,
    ).then((result) => ({
      messages: result.messages.map(normalizeMessage),
      cursor: result.next_cursor,
    }));
  },
  /**
   * Send a message. The server route is discriminated by storage
   * kind — the client passes a body that matches the channel's
   * configured contract.
   */
  send(
    channelXid: string,
    body: SendMessageBody,
    opts?: { idempotencyKey?: string },
  ) {
    return fetchJson<{ message: ServerMessage }>(
      `/v1/channels/${encodeURIComponent(channelXid)}/messages`,
      {
        method: "POST",
        body: messageRequest(body),
        idempotencyKey: opts?.idempotencyKey,
      },
    ).then((result) => normalizeMessage(result.message));
  },
  edit(messageXid: string, body: SendMessageBody) {
    return fetchJson<ServerMessage>(
      `/v1/messages/${encodeURIComponent(messageXid)}`,
      { method: "PATCH", body: messageRequest(body) },
    ).then(normalizeMessage);
  },
  delete(messageXid: string) {
    return fetchJson<void>(
      `/v1/messages/${encodeURIComponent(messageXid)}`,
      { method: "DELETE" },
    );
  },
  react(messageXid: string, body: { emoji: string }) {
    return fetchJson<void>(
      `/v1/messages/${encodeURIComponent(messageXid)}/reactions`,
      { method: "POST", body },
    );
  },
  unreact(messageXid: string, emoji: string) {
    return fetchJson<void>(
      `/v1/messages/${encodeURIComponent(messageXid)}/reactions`,
      { method: "DELETE", body: { emoji } },
    );
  },
  pin(channelXid: string, messageXid: string) {
    return fetchJson<{ ok: true }>(
      `/v1/channels/${encodeURIComponent(channelXid)}/pins`,
      { method: "POST", body: { message_xid: messageXid } },
    );
  },
  unpin(channelXid: string, messageXid: string) {
    return fetchJson<{ ok: true }>(
      `/v1/channels/${encodeURIComponent(channelXid)}/pins/${encodeURIComponent(messageXid)}`,
      { method: "DELETE" },
    );
  },
};

export type SendMessageBody =
  | {
      storage_kind: "atrest";
      body: string;
      parent_message_xid?: string;
      attachment_xids?: string[];
      mentions?: string[];
    }
  | {
      storage_kind: "e2e";
      envelope: string;
      envelope_kind?: "e2e" | "secrets";
      e2e_context_id?: string;
      parent_message_xid?: string;
      expires_at?: string;
      attachment_xids?: string[];
    };

// ---------------------------------------------------------------------------
// DMs
// ---------------------------------------------------------------------------

export interface Dm {
  xid: string;
  is_group: boolean;
  owner_account_id: string | null;
  name: string | null;
  participants: Array<{
    account_xid: string;
    username: string;
    display_name: string;
    avatar_url: string | null;
    banner_color: string;
    banner_mode: "solid" | "gradient";
    banner_secondary_color: string;
    pronouns: string | null;
    member_since: string;
    badges: string[];
    community_tag: CommunityTagSummary | null;
    public_key_b64: string;
    account_id_hash_b64: string;
    presence: "online" | "away" | "busy" | "offline";
  }>;
  last_read_message_xid: string | null;
  latest_message_xid: string | null;
  latest_message_at?: string | null;
  unread_count: number;
  pinned: boolean;
  muted: boolean;
  created_at: string;
}

interface ServerDm {
  xid: string;
  is_group: boolean;
  owner_account_id: string | null;
  name: string | null;
  participants: Array<{
    account_id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
    banner_color: string;
    banner_mode: "solid" | "gradient";
    banner_secondary_color: string;
    pronouns: string | null;
    member_since: string;
    badges: string[];
    community_tag: CommunityTagSummary | null;
    public_key_b64: string;
    account_id_hash_b64: string;
    presence: "online" | "away" | "busy" | "offline";
  }>;
  last_read_message_xid: string | null;
  latest_message_xid: string | null;
  latest_message_at?: string | null;
  unread_count: number;
  pinned: boolean;
  muted: boolean;
  created_at: string;
}

function normalizeDm(dm: ServerDm): Dm {
  return {
    xid: dm.xid,
    is_group: dm.is_group,
    owner_account_id: dm.owner_account_id,
    name: dm.name,
    participants: dm.participants.map((participant) => ({
      account_xid: participant.account_id,
      username: participant.username,
      display_name: participant.display_name ?? participant.account_id,
      avatar_url: participant.avatar_url,
      banner_color: participant.banner_color,
      banner_mode: participant.banner_mode,
      banner_secondary_color: participant.banner_secondary_color,
      pronouns: participant.pronouns,
      member_since: participant.member_since,
      badges: participant.badges,
      community_tag: participant.community_tag,
      public_key_b64: participant.public_key_b64,
      account_id_hash_b64: participant.account_id_hash_b64,
      presence: participant.presence,
    })),
    last_read_message_xid: dm.last_read_message_xid,
    latest_message_xid: dm.latest_message_xid,
    latest_message_at: dm.latest_message_at,
    unread_count: dm.unread_count,
    pinned: dm.pinned,
    muted: dm.muted,
    created_at: dm.created_at,
  };
}

const dms = {
  list() {
    return fetchJson<{ dms: ServerDm[] }>("/v1/dms").then((result) => ({
      dms: result.dms.map(normalizeDm),
    }));
  },
  create(body: { participant_xids: string[]; name?: string; is_group?: boolean }) {
    return fetchJson<{ dm: ServerDm }>("/v1/dms", {
      method: "POST",
      body: {
        participant_account_ids: body.participant_xids,
        ...(body.name !== undefined ? { name: body.name } : {}),
        ...(body.is_group !== undefined ? { is_group: body.is_group } : {}),
      },
    }).then((result) => normalizeDm(result.dm));
  },
  setLastRead(dmXid: string, body: { last_read_message_xid: string }) {
    return fetchJson<{ ok: true }>(
      `/v1/dms/${encodeURIComponent(dmXid)}/last-read`,
      {
        method: "PATCH",
        body: { message_xid: body.last_read_message_xid },
      },
    );
  },
  updateSettings(
    dmXid: string,
    body: { pinned?: boolean; muted?: boolean; hidden?: boolean },
  ) {
    return fetchJson<{ pinned: boolean; muted: boolean; hidden: boolean }>(
      `/v1/dms/${encodeURIComponent(dmXid)}/settings`,
      { method: "PATCH", body },
    );
  },
  messages(dmXid: string, opts?: { before?: string; limit?: number }) {
    const qs = new URLSearchParams();
    if (opts?.before !== undefined) qs.set("cursor", opts.before);
    if (opts?.limit !== undefined) qs.set("limit", opts.limit.toString());
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return fetchJson<{ messages: ServerMessage[]; next_cursor: string | null }>(
      `/v1/dms/${encodeURIComponent(dmXid)}/messages${suffix}`,
    ).then((result) => ({
      messages: result.messages.map(normalizeMessage),
      cursor: result.next_cursor,
    }));
  },
  sendMessage(
    dmXid: string,
    body: Extract<SendMessageBody, { storage_kind: "e2e" }>,
    opts?: { idempotencyKey?: string },
  ) {
    return fetchJson<{ message: ServerMessage }>(
      `/v1/dms/${encodeURIComponent(dmXid)}/messages`,
      {
        method: "POST",
        body: { ...messageRequest(body), channel_type: "text" },
        idempotencyKey: opts?.idempotencyKey,
      },
    ).then((result) => normalizeMessage(result.message));
  },
  editMessage(
    dmXid: string,
    messageXid: string,
    body: Extract<SendMessageBody, { storage_kind: "e2e" }>,
  ) {
    return fetchJson<{ message: ServerMessage }>(
      `/v1/dms/${encodeURIComponent(dmXid)}/messages/${encodeURIComponent(messageXid)}`,
      { method: "PATCH", body: { ...messageRequest(body), channel_type: "text" } },
    ).then((result) => normalizeMessage(result.message));
  },
  deleteMessage(dmXid: string, messageXid: string) {
    return fetchJson<void>(
      `/v1/dms/${encodeURIComponent(dmXid)}/messages/${encodeURIComponent(messageXid)}`,
      { method: "DELETE" },
    );
  },
  rename(dmXid: string, name: string) {
    return fetchJson<{ name: string }>(`/v1/dms/${encodeURIComponent(dmXid)}/group`, { method: "PATCH", body: { name } });
  },
  addParticipant(dmXid: string, accountXid: string) {
    return fetchJson<{ added: boolean }>(`/v1/dms/${encodeURIComponent(dmXid)}/participants`, { method: "POST", body: { account_id: accountXid } });
  },
  removeParticipant(dmXid: string, accountXid: string) {
    return fetchJson<void>(`/v1/dms/${encodeURIComponent(dmXid)}/participants/${encodeURIComponent(accountXid)}`, { method: "DELETE" });
  },
  pins(dmXid: string) {
    return fetchJson<{ message_xids: string[] }>(`/v1/dms/${encodeURIComponent(dmXid)}/pins`);
  },
  pin(dmXid: string, messageXid: string) {
    return fetchJson<void>(`/v1/dms/${encodeURIComponent(dmXid)}/pins/${encodeURIComponent(messageXid)}`, { method: "POST" });
  },
  unpin(dmXid: string, messageXid: string) {
    return fetchJson<void>(`/v1/dms/${encodeURIComponent(dmXid)}/pins/${encodeURIComponent(messageXid)}`, { method: "DELETE" });
  },
};

// ---------------------------------------------------------------------------
// Friends
// ---------------------------------------------------------------------------

interface Friend {
  account_xid: string;
  username?: string;
  display_name: string | null;
  avatar_url: string | null;
  banner_url: string | null;
  banner_color?: string;
  banner_mode?: "solid" | "gradient";
  banner_secondary_color?: string;
  custom_status?: string | null;
  pronouns?: string | null;
  member_since?: string;
  badges: string[];
  community_tag: CommunityTagSummary | null;
  status: "pending" | "accepted" | "blocked";
  direction: "incoming" | "outgoing";
  presence: "online" | "away" | "busy" | "offline";
  since: string;
}

interface ServerFriend {
  account_id: string;
  username?: string;
  display_name: string | null;
  avatar_url: string | null;
  banner_color?: string;
  banner_mode?: "solid" | "gradient";
  banner_secondary_color?: string;
  custom_status?: string | null;
  pronouns?: string | null;
  member_since?: string;
  badges: string[];
  community_tag: CommunityTagSummary | null;
  status: "pending" | "accepted" | "blocked";
  direction: "incoming" | "outgoing";
  presence: "online" | "away" | "busy" | "offline";
  since: string;
}

function normalizeFriend(friend: ServerFriend): Friend {
  return {
    account_xid: friend.account_id,
    username: friend.username,
    display_name: friend.display_name,
    avatar_url: friend.avatar_url,
    banner_url: null,
    banner_color: friend.banner_color,
    banner_mode: friend.banner_mode,
    banner_secondary_color: friend.banner_secondary_color,
    custom_status: friend.custom_status,
    pronouns: friend.pronouns,
    member_since: friend.member_since,
    badges: friend.badges,
    community_tag: friend.community_tag,
    status: friend.status,
    direction: friend.direction,
    presence: friend.presence,
    since: friend.since,
  };
}

const friends = {
  list() {
    return fetchJson<{ friends: ServerFriend[] }>("/v1/friends").then(
      (result) => ({ friends: result.friends.map(normalizeFriend) }),
    );
  },
  request(body: { account_xid: string }) {
    return fetchJson<{ ok: true }>("/v1/friends/requests", {
      method: "POST",
      body: { account_id: body.account_xid },
    });
  },
  accept(requestXid: string) {
    return fetchJson<{ ok: true }>(
      `/v1/friends/requests/${encodeURIComponent(requestXid)}/accept`,
      { method: "POST" },
    );
  },
  reject(requestXid: string) {
    return fetchJson<{ ok: true }>(
      `/v1/friends/requests/${encodeURIComponent(requestXid)}/reject`,
      { method: "POST" },
    );
  },
  block(body: { account_xid: string }) {
    return fetchJson<{ ok: true }>(
      `/v1/friends/${encodeURIComponent(body.account_xid)}/block`,
      {
      method: "POST",
        body: { account_id: body.account_xid },
      },
    );
  },
  unblock(accountXid: string) {
    return fetchJson<{ ok: true }>(
      `/v1/friends/${encodeURIComponent(accountXid)}/unblock`,
      { method: "POST" },
    );
  },
  remove(accountXid: string) {
    return fetchJson<void>(`/v1/friends/${encodeURIComponent(accountXid)}`, {
      method: "DELETE",
    });
  },
  report(
    accountXid: string,
    body: { reason: "spam" | "harassment" | "csam" | "illegal" | "other"; note?: string },
  ) {
    return fetchJson<{ ok: true; report_xid: string }>(
      `/v1/friends/${encodeURIComponent(accountXid)}/report`,
      { method: "POST", body },
    );
  },
};

// ---------------------------------------------------------------------------
// Uploads / Attachments
// ---------------------------------------------------------------------------

interface PresignResponse {
  upload_id: string;
  presigned_put_url: string;
  expires_at: string;
}

interface FinalizeResponse {
  attachment: {
    xid: string;
    message_xid: string | null;
    storage_kind: "e2e" | "atrest";
    filename: string;
    folder_path: string | null;
    mime_type: string;
    byte_size: number;
    sha256_b64: string;
    created_at: string;
    finalized_at: string | null;
  };
}

export interface VaultFile {
  xid: string;
  filename: string;
  folder_path: string | null;
  mime_type: string;
  byte_size: number;
  storage_kind: "e2e" | "atrest";
  channel_xid: string;
  bound_to_message: boolean;
  created_at: string;
  finalized_at: string;
}

const uploads = {
  list() {
    return fetchJson<{ files: VaultFile[] }>("/v1/files");
  },
  usage() {
    return fetchJson<{
      used_bytes: number;
      file_count: number;
      quota_bytes: number;
    }>("/v1/files/usage");
  },
  presign(body: {
    filename: string;
    mime: string;
    byte_size: number;
    channel_xid: string;
    folder_path?: string;
    e2e_key_envelope_b64?: string;
    e2e_context_id?: string;
  }) {
    return fetchJson<PresignResponse>("/v1/files/uploads", {
      method: "POST",
      body,
    });
  },
  finalize(uploadId: string, body: { sha256_b64: string }) {
    return fetchJson<FinalizeResponse>(
      `/v1/files/uploads/${encodeURIComponent(uploadId)}/finalize`,
      { method: "POST", body },
    );
  },
  /**
   * Returns Link's protected attachment-content endpoint. Callers must fetch
   * it with the current bearer token rather than navigate to it directly.
   */
  downloadUrl(attachmentXid: string): string {
    return `/v1/attachments/${encodeURIComponent(attachmentXid)}/download`;
  },
  metadata(attachmentXid: string) {
    return fetchJson<{
      xid: string;
      storage_kind: "e2e" | "atrest";
      filename: string;
      mime_type: string;
      byte_size: number;
      channel_xid: string;
      e2e_context_id: string | null;
      e2e_key_envelope_b64: string | null;
    }>(`/v1/files/${encodeURIComponent(attachmentXid)}/metadata`);
  },
  delete(attachmentXid: string) {
    return fetchJson<{ ok: true }>(
      `/v1/files/${encodeURIComponent(attachmentXid)}`,
      { method: "DELETE" },
    );
  },
};

export type CallParticipantState =
  | "calling"
  | "ringing"
  | "accepted"
  | "declined"
  | "missed"
  | "left";

export interface CallAccountSummary {
  account_xid: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
}

export interface CallSummary {
  call_xid: string;
  dm_xid: string;
  key_context_id: string;
  key_envelope_b64: string;
  with_video: boolean;
  status: "ringing" | "active" | "ended" | "cancelled";
  participant_state: CallParticipantState;
  initiator: CallAccountSummary;
  participants: Array<CallAccountSummary & { state: CallParticipantState }>;
  started_at: string;
  answered_at: string | null;
  ended_at: string | null;
  notification_read_at: string | null;
}

export type CallHistoryEntry = Omit<
  CallSummary,
  "key_context_id" | "key_envelope_b64" | "notification_read_at"
>;

const calls = {
  list() {
    return fetchJson<{ calls: CallSummary[] }>("/v1/calls");
  },
  history(dmXid: string) {
    return fetchJson<{ calls: CallHistoryEntry[] }>(
      `/v1/calls/history/${encodeURIComponent(dmXid)}`,
    );
  },
  start(body: {
    dm_xid: string;
    with_video: boolean;
    key_context_id: string;
    key_envelope_b64: string;
  }) {
    return fetchJson<{ call: CallSummary }>("/v1/calls", { method: "POST", body });
  },
  accept(callXid: string) {
    return fetchJson<{ call: CallSummary }>(`/v1/calls/${encodeURIComponent(callXid)}/accept`, { method: "POST" });
  },
  decline(callXid: string) {
    return fetchJson<{ call: CallSummary }>(`/v1/calls/${encodeURIComponent(callXid)}/decline`, { method: "POST" });
  },
  cancel(callXid: string) {
    return fetchJson<{ call: CallSummary }>(`/v1/calls/${encodeURIComponent(callXid)}/cancel`, { method: "POST" });
  },
  leave(callXid: string) {
    return fetchJson<{ call: CallSummary }>(`/v1/calls/${encodeURIComponent(callXid)}/leave`, { method: "POST" });
  },
  heartbeat(callXid: string) {
    return fetchJson<{ ok: true }>(`/v1/calls/${encodeURIComponent(callXid)}/heartbeat`, { method: "POST" });
  },
  markRead(callXid: string) {
    return fetchJson<{ ok: true }>(`/v1/calls/${encodeURIComponent(callXid)}/read`, { method: "POST" });
  },
};

const media = {
  token(body: { context: "space" | "dm"; target_xid: string; call_xid?: string }) {
    return fetchJson<{
      token: string;
      ws_url: string;
      room_name: string;
      expires_in: number;
    }>("/v1/media/token", { method: "POST", body });
  },
};

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

const search = {
  query(opts: {
    q: string;
    spaceXid?: string;
    channelXid?: string;
    channelType?: "text" | "snippets" | "files";
    authorXid?: string;
    after?: string;
    before?: string;
  }) {
    const qs = new URLSearchParams({ q: opts.q });
    if (opts.spaceXid !== undefined) qs.set("space_xid", opts.spaceXid);
    if (opts.channelXid !== undefined) qs.set("channel_xid", opts.channelXid);
    if (opts.channelType !== undefined) qs.set("channel_type", opts.channelType);
    if (opts.authorXid !== undefined) qs.set("author_xid", opts.authorXid);
    if (opts.after !== undefined) qs.set("after", opts.after);
    if (opts.before !== undefined) qs.set("before", opts.before);
    return fetchJson<{
      results: Array<{
        message_xid: string;
        space_xid: string;
        space_name: string;
        channel_xid: string;
        channel_name: string;
        channel_type: "text" | "snippet" | "file" | "secret";
        author_account_xid: string;
        author_display_name: string;
        created_at: string;
        snippet: string;
      }>;
    }>(`/v1/search?${qs.toString()}`);
  },
};

export interface LinkNotification {
  xid: string; kind: string; title: string; body: string; data: Record<string, unknown>;
  created_at: string; read_at: string | null;
}

const releases = {
  current() {
    return fetchJson<{
      current_version: string; last_seen_version: string | null; needs_acknowledgement: boolean;
      releases: Array<{ version: string; title: string; published_at: string; summary: string; sections: Array<{ title: string; items: string[] }> }>;
    }>("/v1/releases", { cache: "no-store" });
  },
  acknowledge(version: string) {
    return fetchJson<{ ok: true; version: string }>("/v1/releases/acknowledge", { method: "POST", body: { version } });
  },
};

const notifications = {
  list() { return fetchJson<{ unread_count: number; notifications: LinkNotification[] }>("/v1/notifications"); },
  read(xid: string) { return fetchJson<{ ok: true }>(`/v1/notifications/${encodeURIComponent(xid)}/read`, { method: "POST" }); },
  readAll() { return fetchJson<{ ok: true }>("/v1/notifications/read-all", { method: "POST" }); },
};

const invitePreviews = {
  get(token: string) {
    return fetchJson<{ token: string; space: { xid: string; name: string; icon_url: string | null; online_count: number; member_count: number; created_at: string }; is_member: boolean }>(`/v1/invite-previews/${encodeURIComponent(token)}`);
  },
};

// ---------------------------------------------------------------------------
// Public barrel
// ---------------------------------------------------------------------------

export const api = {
  auth,
  me,
  spaces,
  channels,
  messages,
  dms,
  friends,
  communityTags,
  uploads,
  calls,
  media,
  search,
  releases,
  notifications,
  invitePreviews,
};

// Re-export for convenience: callers that import `api` can also pull
// the error class without a second import.
export { ApiCallError } from "./http";
export type { ApiError };
